import { Field } from 'common/fields/entities/field'
import { FieldStats } from 'shared/api/requests/aiEngagementGoal/schemas'

export type StatsItemWithField = FieldStats & { field?: Field }

export const sortCustomUserFields = (itemA: StatsItemWithField, itemB: StatsItemWithField) => {
  const hasFieldA = Boolean(itemA.field)
  const hasFieldB = Boolean(itemB.field)

  if (!hasFieldA && !hasFieldB) {
    return itemB.stats.saved - itemA.stats.saved
  }

  if (!hasFieldA) return 1
  if (!hasFieldB) return -1

  return itemB.stats.saved - itemA.stats.saved
}

export const addFields =
  (fields: Field[]) =>
  (item: FieldStats): StatsItemWithField => {
    const field = fields.find(({ id }) => id === item.data.id)
    return {
      ...item,
      field,
    }
  }

export const sortCollectedFields = (
  fullArray: StatsItemWithField[],
  fieldsOrder: string[],
  fields: Field[],
): StatsItemWithField[] => {
  const orderSet = new Set(fieldsOrder)

  const getSaved = (item: StatsItemWithField) => item.stats?.saved ?? 0

  const collectedMap = new Map<string, StatsItemWithField>()
  const previouslyCollectedFields: StatsItemWithField[] = []
  const deletedFields: StatsItemWithField[] = []

  for (const item of fullArray) {
    if (!item.field) {
      deletedFields.push(item)
      continue
    }
    if (orderSet.has(item.field.id)) {
      collectedMap.set(item.field.id, item)
    } else {
      previouslyCollectedFields.push(item)
    }
  }

  const currentCollectedFields: StatsItemWithField[] = fieldsOrder
    .map((id) => collectedMap.get(id))
    .filter((field): field is StatsItemWithField => Boolean(field))

  const existingIds = new Set(fullArray.filter(({ field }) => field).map(({ field }) => field?.id))
  const currentUncollectedFieldsIds = fieldsOrder.filter((id) => !existingIds.has(id))
  const currentUncollectedFields = []

  for (const itemId of currentUncollectedFieldsIds) {
    const field = fields.find((field) => field.id === itemId)

    if (!field) {
      deletedFields.push({
        data: {
          id: itemId,
        },
        stats: { saved: 0 },
      })
    } else {
      currentUncollectedFields.push({
        field,
        data: {
          id: itemId,
        },
        stats: { saved: 0 },
      })
    }
  }

  const bySavedDesc = (current: StatsItemWithField, previous: StatsItemWithField) =>
    getSaved(previous) - getSaved(current)

  currentUncollectedFields.sort(bySavedDesc)
  previouslyCollectedFields.sort(bySavedDesc)
  deletedFields.sort(bySavedDesc)

  return [
    ...currentCollectedFields,
    ...currentUncollectedFields,
    ...previouslyCollectedFields,
    ...deletedFields,
  ]
}
